import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WaitlistForm from '../../components/WaitlistForm';
import { trackCta } from '../../lib/analytics';

const features = [
  {
    title: 'One email per parent — automatically',
    body:
      "Pick a meet, pick athletes, hit send. Baton emails each parent one personalized email summarizing every kid they have on the team — events, divisions, scheduled times, venue, check-in instructions.",
  },
  {
    title: 'A real workspace, not a database',
    body:
      "Coaches manage rosters, register athletes into events, build the program, and track entries. Parents and athletes sign in to see their schedule. Admins keep the org tidy.",
  },
  {
    title: 'Mobile-first, modern, fast',
    body:
      "Use it on the bleachers from your phone. No Windows-only desktop installs, no 30-page setup manual, no per-seat licensing.",
  },
  {
    title: 'Made for the 95% of coaches',
    body:
      "Most coaches manage their team — they don't host the meet. Baton is built for them. Run the team. Email the parents. Show up Saturday ready.",
  },
];

const steps = [
  { n: '01', title: 'Sign up your team', body: 'Create an org in 60 seconds. Invite your coaches.' },
  { n: '02', title: 'Add roster + venues', body: 'Bring in athletes, link parents, save your home venues.' },
  { n: '03', title: 'Build the meet', body: 'Add events to a meet, register athletes, set heats and lanes.' },
  { n: '04', title: 'Send the schedule', body: 'Preview, send. Each parent gets one polished email per meet.' },
];

const faqs = [
  {
    q: 'How is this different from HyTek Meet Manager?',
    a: 'HyTek is built for the meet director running the meet — desktop-only, deeply integrated with timing hardware. Baton is built for the coach managing a team across the season: rosters, registrations, parent comms. We complement HyTek; we don\'t replace it.',
  },
  {
    q: 'How is this different from Athletic.net?',
    a: 'Athletic.net is mostly a public results database with some registration on the side. Baton is a private workspace your team actually lives in, with a parent portal that doesn\'t exist on Athletic.net.',
  },
  {
    q: 'How much will it cost?',
    a: 'The first 50 founding coaches get it free for life. After that, expect a low monthly price — built to be cheaper than what you\'re paying for HyTek + Athletic.net + a generic team app combined.',
  },
  {
    q: 'When will it be ready?',
    a: 'The product is already running in private beta. We\'re onboarding founding coaches now and shipping new features weekly based on what they ask for.',
  },
  {
    q: "Is my team's data private?",
    a: 'Yes. Your org is fully isolated from every other org. Parents only see their own kids. Athletes only see their own profile. We never sell or share data.',
  },
];

export default function HomePage() {
  const location = useLocation();

  useEffect(() => {
    document.title = 'Baton — track & field for coaches and parents';
    if (location.hash) {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location]);

  return (
    <Box>
      {/* HERO */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(180deg, rgba(220,234,245,0.55) 0%, rgba(245,247,250,0) 70%)',
          pb: { xs: 6, md: 10 },
          pt: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 7 }} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Chip
                size="small"
                label="Private beta · founding coaches free for life"
                sx={{
                  bgcolor: '#fef7ec',
                  color: '#92400e',
                  fontWeight: 600,
                  border: '1px solid #fbd599',
                }}
              />
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  mt: 2.5,
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  fontSize: { xs: 36, sm: 48, md: 60 },
                  lineHeight: 1.05,
                }}
              >
                Pass the schedule from coach to parent.{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(90deg, #0b5394 0%, #d97706 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    color: 'transparent',
                  }}
                >
                  In one click.
                </Box>
              </Typography>
              <Typography variant="h6" component="p" sx={{ mt: 2.5, color: 'text.secondary', maxWidth: 560, fontWeight: 400 }}>
                Baton is the modern team & meet workspace for track and field — rosters,
                registrations, schedules, and parent emails that don't make you want to throw
                your laptop into the long-jump pit.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                <Button
                  component={RouterLink}
                  to="/#waitlist"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  aria-label="Get early access"
                  onClick={() => trackCta('Waitlist', 'hero')}
                  sx={{ px: 3, py: 1.25 }}
                >
                  Get early access
                </Button>
                <Button
                  component={RouterLink}
                  to="/#how"
                  variant="outlined"
                  size="large"
                  aria-label="See how it works"
                  sx={{ px: 3, py: 1.25 }}
                >
                  See how it works
                </Button>
              </Stack>
              <Stack direction="row" spacing={3} sx={{ mt: 3.5, color: 'text.secondary', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption">✓ Works on your phone</Typography>
                <Typography variant="caption">✓ No timing hardware required</Typography>
                <Typography variant="caption">✓ Free for founding coaches</Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <HeroTrackIllustration />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* PROBLEM / SOLUTION */}
      <Container id="features" maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Grid container spacing={{ xs: 5, md: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 1 }}>
              The problem
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 700 }}>
              Coaching tools are stuck in 2008.
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 3, color: 'text.secondary' }}>
              {[
                'HyTek Meet Manager is Windows-only and built for meet directors, not coaches.',
                'Athletic.net is a public results site, not a private workspace.',
                "Generic team apps (TeamSnap, etc.) don't understand events, heats, or lanes.",
                'Coaches end up living in spreadsheets and Outlook the night before every meet.',
              ].map((line) => (
                <Stack key={line} direction="row" spacing={1.5}>
                  <Box sx={{ mt: 0.9, width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.400', flexShrink: 0 }} />
                  <Typography>{line}</Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1 }}>
              The fix
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 700 }}>
              One workspace. One click to email parents.
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 3, color: 'text.secondary' }}>
              {[
                'Rosters, venues, meets, registrations — all in a clean web app.',
                'Coaches, parents, and athletes each get the right view.',
                'Polished schedule emails to every parent, deduped across siblings.',
                'Mobile-first. Cloud-hosted. No installs.',
              ].map((line) => (
                <Stack key={line} direction="row" spacing={1.5}>
                  <Box sx={{ mt: 0.9, width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.light', flexShrink: 0 }} />
                  <Typography>{line}</Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* FEATURE GRID */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" textAlign="center" sx={{ fontWeight: 700 }}>
            Built for the way you actually coach
          </Typography>
          <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mt: 1.5, maxWidth: 600, mx: 'auto' }}>
            Everything you need to run a season. Nothing you don't.
          </Typography>
          <Grid container spacing={2.5} sx={{ mt: 4 }}>
            {features.map((f) => (
              <Grid item xs={12} md={6} key={f.title}>
                <Card
                  variant="outlined"
                  sx={{ borderRadius: 3, bgcolor: 'rgba(245,247,250,0.6)', borderColor: 'divider' }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {f.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {f.body}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* HOW IT WORKS */}
      <Container id="how" maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Typography variant="h4" textAlign="center" sx={{ fontWeight: 700 }}>
          From signup to "sent" in under 10 minutes
        </Typography>
        <Grid container spacing={2} sx={{ mt: 5 }}>
          {steps.map((s) => (
            <Grid item xs={12} sm={6} md={3} key={s.n}>
              <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 800, letterSpacing: 2 }}>
                    {s.n}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {s.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {s.body}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* COMPARISON BAND */}
      <Box sx={{ bgcolor: '#0f1828', color: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                A clean second opinion on what your team actually uses.
              </Typography>
              <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.75)' }}>
                We wrote an honest comparison of HyTek, Athletic.net, MeetPro, TFxCPro, and TeamSnap —
                what each is great at, what they're weak at, and what to combine if you want to stay sane.
              </Typography>
              <Button
                component={RouterLink}
                to="/compare"
                variant="contained"
                color="inherit"
                onClick={() => trackCta('Compare', 'home_inline')}
                sx={{ mt: 3, color: '#0f1828', bgcolor: '#fff', '&:hover': { bgcolor: '#f5f7fa' } }}
                aria-label="Read the comparison"
              >
                Read the comparison →
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={1.5}>
                {['HyTek Meet Manager', 'Athletic.net', 'DirectAthletics MeetPro', 'TFxCPro', 'TeamSnap', 'OpenTrack'].map((n) => (
                  <Grid item xs={6} key={n}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid rgba(255,255,255,0.10)',
                        bgcolor: 'rgba(255,255,255,0.05)',
                        fontSize: 14,
                      }}
                    >
                      {n}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* WAITLIST */}
      <Container maxWidth="sm" sx={{ py: { xs: 8, md: 10 } }}>
        <WaitlistForm />
      </Container>

      <Divider />

      {/* FAQ */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 10 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
          Questions
        </Typography>
        {faqs.map((f) => (
          <Accordion key={f.q} disableGutters elevation={0} sx={{ borderTop: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: '1px solid', borderColor: 'divider' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{f.q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">{f.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>
    </Box>
  );
}

function HeroTrackIllustration() {
  return (
    <Box
      sx={{
        position: 'relative',
        pt: { xs: 1, md: 2 },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: { xs: '12% 3% auto 3%', md: '14% -6% auto 2%' },
          height: { xs: 240, sm: 300, md: 340 },
          borderRadius: '999px',
          background: 'radial-gradient(circle, rgba(217,119,6,0.18) 0%, rgba(58,125,196,0.16) 44%, rgba(58,125,196,0) 72%)',
          filter: 'blur(4px)',
        }}
        aria-hidden
      />

      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 24px 55px rgba(11,83,148,0.16)',
        }}
      >
        <Box
          component="img"
          src="/images/track-and-field-illustration.png"
          alt="Stylized illustration of a relay runner sprinting on a blue track with hurdles and field events in the background"
          aria-label="Stylized track and field illustration"
          loading="eager"
          sx={{
            display: 'block',
            width: '100%',
            height: { xs: 320, sm: 390, md: 430 },
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      </Box>
    </Box>
  );
}
